import React, { useState, useEffect } from "react";
import { FaCheck, FaCheckCircle, FaShieldAlt } from "react-icons/fa";
import { getPaymentMethodIcon } from "../../utils/paymentDisplayUtils";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";

export default function PaymentDetailsCard({ booking, rawBookingData }) {
  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return "₹0";
    return `₹${parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  // Get payment method display text (without icon)
  const getPaymentMethodDisplay = () => {
    const method = booking?.paymentMethod || rawBookingData?.paymentMethod;
    const advancePaid = rawBookingData?.advancePaid || booking?.advancePaid || 0;
    const isCollected = rawBookingData?.isPaymentCollected || booking?.isPaymentCollected;

    if (advancePaid > 0) {
      if (!isCollected) {
        return "Online Advance (Remaining Pending)";
      } else {
        const methodUpper = (method || "").toUpperCase();
        if (methodUpper === "COD") return "Online Advance & Cash";
        if (methodUpper === "WALLET") return "Online Advance & Wallet";
        if (methodUpper === "ONLINE" || methodUpper === "RAZORPAY") return "Online Advance & Online QR";
        return `Online Advance & ${method}`;
      }
    }

    if (!method) return "Not specified";
    if (method.toUpperCase() === "ONLINE" || method.toUpperCase() === "RAZORPAY") {
      return "Razorpay";
    }
    if (method.toUpperCase() === "COD") {
      return "Cash on Delivery";
    }
    if (method.toUpperCase() === "WALLET") {
      return "Wallet";
    }
    return method;
  };

  // Get payment status color and icon
  const getPaymentStatusDisplay = () => {
    const status = booking?.paymentStatus || rawBookingData?.paymentStatus;
    const advancePaid = rawBookingData?.advancePaid || 0;
    const paymentOption = rawBookingData?.paymentOption || booking?.paymentOption;
    const remaining = rawBookingData?.finalAmount || rawBookingData?.remainingAmount || 0;

    if (!status) return { label: "Pending", color: "bg-amber-50", textColor: "text-amber-700", border: "border-amber-100" };

    const statusLower = status.toLowerCase();
    if (statusLower === "completed") {
      // If this was a partial/advance payment and there's still remaining balance → show "Advance Paid"
      const isCollected = rawBookingData?.isPaymentCollected || booking?.isPaymentCollected;
      const isPartialPayment = advancePaid > 0 && !isCollected && (paymentOption === "partial" || remaining > 0);
      if (isPartialPayment) {
        return { label: "Advance Paid", color: "bg-blue-50", textColor: "text-blue-700", border: "border-blue-100" };
      }
      // Full payment completed → show "Paid"
      return { label: "Paid", color: "bg-emerald-50", textColor: "text-emerald-700", border: "border-emerald-100" };
    }
    if (statusLower === "failed") {
      return { label: "Failed", color: "bg-rose-50", textColor: "text-rose-700", border: "border-rose-100" };
    }
    return { label: "Pending", color: "bg-amber-50", textColor: "text-amber-700", border: "border-amber-100" };
  };

  const paymentStatus = getPaymentStatusDisplay();
  const baseFare = booking?.baseAmount || rawBookingData?.baseAmount || 0;
  let promoDiscount = booking?.couponDiscountAmount || rawBookingData?.couponDiscountAmount || 0;
  const taxAmount = booking?.taxAmount || rawBookingData?.taxAmount || 0;
  let totalAmount = booking?.totalAmount || rawBookingData?.totalAmount || 0;
  let addOnsCost = booking?.addOnsCost || rawBookingData?.addOnsCost || 0;

  // Extra charges from driver on completion
  const extraKmCharge = rawBookingData?.extraKmCharge || 0;
  const extraKm = rawBookingData?.extraKm || 0;
  const waitingCharge = rawBookingData?.waitingCharge || 0;
  const waitingTime = rawBookingData?.waitingTime || 0;
  
  // Total ride fare including extra charges
  const totalRideFare = totalAmount + extraKmCharge + waitingCharge;
  const remainingPayable = rawBookingData?.finalAmount || (totalRideFare - (rawBookingData?.advancePaid || 0));

  // Get coupon code if available
  const couponCode = booking?.couponCode || rawBookingData?.couponCode;

  // Correct calculation: totalAmount should equal baseFare - promoDiscount + taxAmount + addOnsCost
  const expectedTotal = Math.round(baseFare - promoDiscount + taxAmount + addOnsCost);

  // Check for discrepancies - only reconcile if values appear incomplete
  if (Math.abs(expectedTotal - totalAmount) > 1) {
    if (couponCode && promoDiscount === 0 && addOnsCost > 0) {
      const inferredDiscount = Math.round(baseFare + taxAmount + addOnsCost - totalAmount);
      if (inferredDiscount > 0 && inferredDiscount <= baseFare * 0.5) {
        promoDiscount = inferredDiscount;
      }
    }
    else if (addOnsCost === 0 && totalAmount > 0) {
      const inferredAddOns = Math.round(Math.max(0, totalAmount - baseFare + promoDiscount - taxAmount));
      if (inferredAddOns >= 0) {
        addOnsCost = inferredAddOns;
      }
    }
    // Recalculate total to ensure consistency
    totalAmount = Math.round(baseFare - promoDiscount + taxAmount + addOnsCost);
  }

  const isWallet = (booking?.paymentMethod || rawBookingData?.paymentMethod)?.toUpperCase() === "WALLET";
  const isCod = (booking?.paymentMethod || rawBookingData?.paymentMethod)?.toUpperCase() === "COD";

  return (
    <div className="w-full lg:w-[320px] lg:ml-auto bg-gradient-to-br from-white to-slate-50/50 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)] border border-slate-100/80 p-6 flex flex-col transition-all duration-300 ease-out group">
      
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-[15px] font-bold text-slate-800 tracking-tight uppercase">
          Payment Details
        </h2>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${paymentStatus.color} ${paymentStatus.textColor} ${paymentStatus.border}`}>
          {paymentStatus.label}
        </span>
      </div>

      {/* Price Breakdown Container */}
      <div className="space-y-3.5 text-slate-600 text-sm flex-1">
        <div className="flex justify-between items-center">
          <span className="text-[13px] text-slate-400 font-medium">Base Fare</span>
          <span className="font-semibold text-slate-700">
            {formatCurrency(baseFare)}
          </span>
        </div>

        {promoDiscount > 0 && (
          <div className="flex justify-between items-center text-emerald-600 bg-emerald-50/40 px-2 py-1.5 rounded-lg border border-emerald-100/30 text-xs">
            <span className="font-medium">Promo Discount ({couponCode || "Applied"})</span>
            <span className="font-bold">-{formatCurrency(promoDiscount)}</span>
          </div>
        )}

        {taxAmount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-[13px] text-slate-400 font-medium">GST (18%)</span>
            <span className="font-semibold text-slate-700">
              {formatCurrency(taxAmount)}
            </span>
          </div>
        )}

        {/* Display individual add-ons if available */}
        {rawBookingData?.selectedAddOns && Array.isArray(rawBookingData.selectedAddOns) && rawBookingData.selectedAddOns.length > 0 && (
          <div className="border-t border-slate-100 pt-3">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Selected Add-ons</p>
            <div className="space-y-2.5">
              {rawBookingData.selectedAddOns.map((addOn, index) => (
                <div key={index} className="flex justify-between items-center text-xs">
                  <span className="text-slate-500 font-medium">{addOn.name || addOn.description || "Add-on"}</span>
                  <span className="font-semibold text-slate-700">
                    {formatCurrency(addOn.price || 0)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fallback: Show total add-ons cost if individual add-ons not available */}
        {addOnsCost > 0 && (!rawBookingData?.selectedAddOns || !Array.isArray(rawBookingData.selectedAddOns) || rawBookingData.selectedAddOns.length === 0) && (
          <div className="flex justify-between items-center">
            <span className="text-[13px] text-slate-400 font-medium">Add-ons</span>
            <span className="font-semibold text-slate-700">
              {formatCurrency(addOnsCost)}
            </span>
          </div>
        )}

        {/* Extra Distance Charge */}
        {extraKmCharge > 0 && (
          <div className="flex justify-between items-center text-amber-700 bg-amber-50/50 px-2.5 py-2 rounded-xl border border-amber-100/50 text-xs">
            <span className="font-semibold flex items-center gap-1">🚗 Extra Distance ({extraKm} km)</span>
            <span className="font-bold">+{formatCurrency(extraKmCharge)}</span>
          </div>
        )}

        {/* Waiting Charge */}
        {waitingCharge > 0 && (
          <div className="flex justify-between items-center text-amber-700 bg-amber-50/50 px-2.5 py-2 rounded-xl border border-amber-100/50 text-xs">
            <span className="font-semibold flex items-center gap-1">⏳ Waiting Charge ({waitingTime} mins)</span>
            <span className="font-bold">+{formatCurrency(waitingCharge)}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-dashed border-slate-200 my-4"></div>

      {/* Fare Summary */}
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm font-bold text-slate-700">
          {rawBookingData?.advancePaid > 0 ? "Total Ride Fare" : "Total Due"}
        </span>
        <span className="text-lg font-extrabold text-slate-800 tracking-tight">
          {formatCurrency(totalRideFare)}
        </span>
      </div>

      {/* Partial Payment Highlight Box */}
      {rawBookingData?.advancePaid > 0 && (() => {
        const isCollected = rawBookingData?.isPaymentCollected || booking?.isPaymentCollected;
        const method = (booking?.paymentMethod || rawBookingData?.paymentMethod || "").toUpperCase();
        let remainingMethodText = "Payable at destination";
        if (isCollected) {
          if (method === "COD") remainingMethodText = "Paid via Cash";
          else if (method === "WALLET") remainingMethodText = "Paid via Wallet";
          else if (method === "ONLINE" || method === "RAZORPAY") remainingMethodText = "Paid via Online QR";
          else remainingMethodText = `Paid via ${method}`;
        }

        return (
          <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-3.5 mt-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Payment Split</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                isCollected 
                  ? "bg-emerald-50 text-emerald-700 border-emerald-100" 
                  : "bg-blue-50 text-blue-700 border-blue-100"
              } flex items-center gap-1`}>
                <span className="text-[9px]">✓</span> {isCollected ? "Fully Paid" : "Advance Paid"}
              </span>
            </div>
            
            <div className="flex justify-between items-center text-sm">
              <span className="text-[13px] text-slate-500 font-medium">Part Pay (Advance)</span>
              <span className="font-bold text-emerald-600">
                {formatCurrency(rawBookingData.advancePaid)}
              </span>
            </div>
            
            <div className="border-t border-slate-200/50 pt-3 flex justify-between items-center text-sm">
              <div>
                <span className="font-bold text-slate-700 block">Remaining Amount</span>
                <span className="text-[10px] text-slate-400 font-medium">{remainingMethodText}</span>
              </div>
              <span className={`text-base font-extrabold ${isCollected ? "text-emerald-600" : "text-slate-800"}`}>
                {formatCurrency(remainingPayable)}
              </span>
            </div>
          </div>
        );
      })()}

      {/* Refund Details for Cancelled Bookings */}
      {rawBookingData?.status?.toLowerCase() === "cancelled" && (
        <>
          {rawBookingData.refundStatus === "processed" ? (
            <div className="bg-emerald-50/60 border border-emerald-100 rounded-2xl p-4 mt-4 text-xs text-emerald-800">
              <div className="font-bold flex items-center gap-1.5 mb-1.5 text-[13px]">
                <span className="text-sm">✓</span> Refund Processed
              </div>
              <p className="leading-relaxed">
                A refund of <strong className="font-bold text-emerald-900">{formatCurrency(rawBookingData.refundAmount || rawBookingData.advancePaid || totalAmount)}</strong> has been processed to your {
                  isWallet ? "wallet" : "original payment method"
                }.
              </p>
              {rawBookingData.refundProcessedAt && (
                <p className="text-[10px] text-emerald-600/70 mt-2">
                  Processed: {new Date(rawBookingData.refundProcessedAt).toLocaleString("en-IN")}
                </p>
              )}
            </div>
          ) : (booking?.paymentMethod || rawBookingData?.paymentMethod)?.toUpperCase() !== "COD" && (rawBookingData.advancePaid > 0 || rawBookingData.paymentStatus === "completed") ? (
            <div className="bg-amber-50/60 border border-amber-100 rounded-2xl p-4 mt-4 text-xs text-amber-800">
              <div className="font-bold flex items-center gap-1.5 mb-1 text-[13px]">
                <span className="text-sm">⏳</span> Refund Pending
              </div>
              <p className="leading-relaxed">Your refund is being processed and will reflect in your account shortly.</p>
            </div>
          ) : null}
        </>
      )}

      {/* Payment Method Banner */}
      {(() => {
        const advancePaid = rawBookingData?.advancePaid || booking?.advancePaid || 0;
        const isCollected = rawBookingData?.isPaymentCollected || booking?.isPaymentCollected;
        const methodUpper = (booking?.paymentMethod || rawBookingData?.paymentMethod || "").toUpperCase();

        // Determine styling for the bottom banner
        let bannerBgClass = "bg-gradient-to-r from-sky-50/70 to-blue-50/30 border-sky-100/50 shadow-sm shadow-sky-50/10";
        let iconBgClass = "bg-gradient-to-br from-sky-500 to-blue-600 shadow-sky-100";
        let iconMethod = booking?.paymentMethod || rawBookingData?.paymentMethod;

        if (advancePaid > 0) {
          if (!isCollected) {
            bannerBgClass = "bg-gradient-to-r from-blue-50/70 to-sky-50/30 border-blue-100/50 shadow-sm shadow-blue-50/10";
            iconBgClass = "bg-gradient-to-br from-blue-500 to-sky-600 shadow-blue-100";
            iconMethod = "ONLINE";
          } else {
            if (methodUpper === "COD") {
              bannerBgClass = "bg-gradient-to-r from-amber-50/70 to-blue-50/30 border-amber-100/50 shadow-sm shadow-amber-50/10";
              iconBgClass = "bg-gradient-to-br from-amber-500 to-blue-600 shadow-amber-100";
              iconMethod = "COD";
            } else if (methodUpper === "WALLET") {
              bannerBgClass = "bg-gradient-to-r from-purple-50/70 to-blue-50/30 border-purple-100/50 shadow-sm shadow-purple-50/10";
              iconBgClass = "bg-gradient-to-br from-purple-500 to-blue-600 shadow-purple-100";
              iconMethod = "WALLET";
            } else {
              bannerBgClass = "bg-gradient-to-r from-sky-50/70 to-blue-50/30 border-sky-100/50 shadow-sm shadow-sky-50/10";
              iconBgClass = "bg-gradient-to-br from-sky-500 to-blue-600 shadow-sky-100";
              iconMethod = "ONLINE";
            }
          }
        } else {
          if (isWallet) {
            bannerBgClass = "bg-gradient-to-r from-blue-50/70 to-indigo-50/30 border-blue-100/50 shadow-sm shadow-blue-50/10";
            iconBgClass = "bg-gradient-to-br from-indigo-500 to-blue-600 shadow-indigo-100";
          } else if (isCod) {
            bannerBgClass = "bg-gradient-to-r from-amber-50/70 to-orange-50/30 border-amber-100/50 shadow-sm shadow-amber-50/10";
            iconBgClass = "bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-100";
          }
        }

        return (
          <div className={`rounded-2xl p-4 flex items-center gap-3.5 mt-5 border transition-all duration-300 ${bannerBgClass}`}>
            <div className={`p-2.5 rounded-xl text-white flex items-center justify-center shadow-md ${iconBgClass}`}>
              {getPaymentMethodIcon(iconMethod, "w-4 h-4")}
            </div>

            <div className="flex-1 min-w-0">
              <span className="block text-[10px] font-bold text-slate-400 tracking-wider uppercase">
                Payment Method
              </span>
              <span className="text-[13px] text-slate-700 font-bold block truncate">
                Paid via {getPaymentMethodDisplay()}
              </span>
            </div>

            {paymentStatus.label === "Paid" && (
              <div className="bg-emerald-500 text-white rounded-full p-1 flex items-center justify-center shadow-sm">
                <FaCheck className="w-2.5 h-2.5" />
              </div>
            )}
          </div>
        );
      })()}

      {/* Secured Payment Badge */}
      <div className="flex items-center justify-center gap-1.5 mt-4 text-[10px] text-slate-400 font-medium">
        <FaShieldAlt className="w-3 h-3 text-slate-300" />
        <span>100% Secured Payment</span>
      </div>

    </div>
  );
}
